import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Bouton. Quatre intentions seulement — au-delà, une interface perd sa
 * hiérarchie et l'œil ne sait plus où aller.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium",
    "rounded-full select-none",
    // `duration-instant` : le retour de pression doit être sous le seuil de
    // perception, sinon le bouton paraît mou.
    "transition-[background-color,color,box-shadow,transform] duration-[120ms] ease-silk",
    "active:scale-[.98]",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:size-[1.15em] [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-blush-500 text-white shadow-soft hover:bg-blush-600",
        secondary: "bg-creme-200 text-encre-800 hover:bg-creme-300",
        ghost: "text-encre-700 hover:bg-creme-200",
        danger: "bg-grenat-500 text-white hover:bg-grenat-700",
      },
      size: {
        sm: "h-9 px-4 text-sm",
        md: "h-11 px-5 text-[15px]",
        lg: "h-13 px-7 text-base",
        /**
         * Cible tactile de Nina : 52px, atteignable les mains occupées et
         * sans précision. Réservée aux actions fréquentes en cabine.
         */
        touch: "min-h-[3.25rem] px-6 text-base w-full",
        icon: "size-11",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Affiche un état de chargement et neutralise le clic. */
    loading?: boolean;
    /**
     * Rend une ANCRE plutôt qu'un bouton.
     *
     * À utiliser dès que l'action est une navigation. Le motif
     * `<Link><Button/></Link>` produit `<a><button>`, que la spécification
     * HTML interdit et qui casse la tabulation — constaté au navigateur sur
     * la galerie et la page d'inspiration.
     */
    href?: string;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, loading, disabled, href, children, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size, block }), className);

  const content = (
    <>
      {loading && (
        <span
          aria-hidden
          className="size-[1em] animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
        />
      )}
      {children}
    </>
  );

  if (href) {
    const { type: _type, ...anchorProps } = props;
    return (
      <a
        ref={ref as unknown as React.Ref<HTMLAnchorElement>}
        href={href}
        className={classes}
        aria-busy={loading || undefined}
        // Une ancre ne se désactive pas : on la retire du flux de tabulation.
        aria-disabled={disabled || loading || undefined}
        {...(anchorProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={props.type ?? "button"}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </button>
  );
});

export { buttonVariants };
